import { defineStore } from 'pinia'
import { api } from 'boot/axios'
import { computed, ref, watch } from 'vue'
import { useNotificationStore } from 'stores/notification'
import { useLocalStorage } from '@vueuse/core'
import { Constants } from 'src/helpers/constants'
import { isArray, isObject, isString, zipObject } from 'lodash'
import { useEnvironmentStore } from 'stores/environment'
import { i18n } from 'boot/i18n'

export const useUserStore = defineStore('user', () => {
  const env = useEnvironmentStore()
  const acl = {}
  // maps acl prefixes to mod names of crm
  // module anon uses kdb acls
  const modTrans = { ...zipObject(Object.values(Constants.Modules), Object.values(Constants.Modules)), anon: 'kdb' }
  const user = ref({})
  const mandants = ref([])
  const token = useLocalStorage('token', null)
  const n = useNotificationStore()
  const currentModul = ref('')
  // Incremented whenever the ACL map is discarded (login, logout, user switch),
  // so consumers can re-evaluate rights for the new identity.
  const aclRevision = ref(0)
  // Pending ACL request, shared by concurrent checks so the map is fetched once.
  let aclLoadPromise = null

  const currentMandant = computed(() => {
    return mandants.value.find(m => m.selected)
  })

  const hasToken = computed(() => {
    return typeof token.value === 'string' && token.value.length > 0
  })

  async function getUserData () {
    try {
      const u = await api.get('sessions/getUserData.json')
      user.value = { ...u.data }
      const m = await api.get('sessions/getMandants.json')
      mandants.value = m.data
    } catch (error) {
      let msg = error.message ?? null
      if (error.response && error.response.data) {
        msg = error.response.data?.message ?? null
      }
      n.error(msg || 'Fehler beim Login')
    }
  }

  async function login (username, password) {
    try {
      const response = await api.post(
        '/sessions.json',
        { username, password }
      )
      token.value = response.data?.token ?? null
    } catch (error) {
      const msg = error.response?.data?.message
        ? error.response.data.message
        : 'Fehler beim Login'
      n.error(msg)
    }
  }

  watch(
    token,
    async (current, old) => {
      if (token.value !== (old?.value ?? null)) {
        // Every token change means another identity (login, logout, user switch):
        // never keep the rights of the previous one.
        resetACL()
        await getUserData()
      }
    },
    {
      immediate: true,
    }
  )

  async function loginByTicket (ticket) {
    try {
      const response = await api.get(
        '/sessions/loginByTicket.json',
        {
          params: { ticket }
        }
      )
      token.value = response.data?.token ?? null
    } catch (error) {
      const msg = error.response.data.message
        ? error.response.data.message
        : 'Fehler beim Login'
      n.error(msg)
    }
  }

  async function changeUser (userId) {
    try {
      const response = await api.get(`sessions/changeUser/${userId}.json`)
      token.value = response.data?.token ?? null
    } catch (error) {
      const msg = error.response.data.message
        ? error.response.data.message
        : 'Fehler beim Mandantwechsel'
      n.error(msg)
    }
  }

  async function logout () {
    token.value = null
  }

  function getAuthHeader () {
    // return authorization header with jwt token
    if (hasToken.value) {
      return { Authorization: `bearer ${token.value}` }
    } else {
      return {}
    }
  }

  function saveCurrentModul(route) {
    currentModul.value = route
  }
  /**
   * returns true iff acl is true regardless of current module
   *
   * @param property
   * @param mode
   * @returns {boolean|*|boolean}
   */
  async function weakHasAcl (property, mode)
  {
    return hasAcl(property, mode, true)
  }

  /**
   * returns true, iff given property evaluates to true
   * property may be:
   *   - the name of a property
   *   - an array of property names, that is true iff all properties are true
   *   - an object with 'and', 'or', 'not' as key and arrays of property names or objects as values
   *     e.g. {and: ['acl_prop_1', {not: ['acl_prop_2']}, {or: ['acl_prop_3', 'acl_prop_4']}]},
   *          {not: {or: ['acl_prop_3', 'acl_prop_4']}}
   */
  async function hasAcl (property, mode = 'and', skipModCheck = false, result = false)
  {
    await loadACL()

    let roundResult, i;
    if (isString(property)) {
      const aclModPrefix = modTrans[property.split('_').shift()]

      if (!skipModCheck && env.currentMod.value !== aclModPrefix) {
        console.log(`${env.currentMod.value}: ${property}`)
      }

      if (skipModCheck && -1 !== property.indexOf('*')) {
        const regex = new RegExp(`^${property.replace('*', '[^_]+')}$`)
        let value = 0
        for (let idx in acl) {
          if (idx.match(regex)) {
            value += acl[idx]
          }
        }
        return 0 < value
      }

      return Object.hasOwn(acl, property)
        && acl[property] === 1
        && (skipModCheck || aclModPrefix === env.currentMod.value)
    }

    if (isArray(property)) {
      roundResult = 'and' === mode;
      for (i = 0; i < property.length; i++) {
        switch (mode) {
          case 'and':
            roundResult = roundResult && hasAcl(property[i], mode, skipModCheck, roundResult);
            break
          case 'or':
            roundResult = roundResult || hasAcl(property[i], mode, skipModCheck, roundResult);
            break
          case 'not':
            roundResult = !hasAcl(property[i], mode, skipModCheck, roundResult);
            break
        }
      }
      return roundResult
    }

    if (isObject(property)) {
      for (i in property) {
        if (Object.hasOwn(property, i)) {
          return hasAcl(property[i], i, skipModCheck, result);
        }
      }
    }

    return false
  }

  /**
   * returns true, iff all (mode = and) or at least one of (mode = or)
   * acl property names starting with the string property are true
   * @param property
   * @param mode
   * @returns {boolean}
   */
  async function matchAcl(property, mode)
  {
    await loadACL()
    mode = mode || 'or';
    if (!isString(property) || !isString(mode)) {
      return false
    }

    mode = mode.toLowerCase();
    let result, fn;
    if (-1 === (result = ['or', 'and'].indexOf(mode))) {
      return false
    }
    fn = [
      // 'or' case
      prop => result+= acl[prop],
      // 'and' case
      prop => result*= acl[prop]
    ][result]

    Object.keys(acl).forEach(propertyName => {
      if (-1 < propertyName.indexOf(property)) {
        fn(propertyName)
      }
    })

    return 0 !== result
  }

  /**
   * returns all acl properties whose names contain needle and for which has(property, 'and', skipModCheck) returns true
   * @param needle
   * @param skipModCheck
   * @returns {*[]}
   * @see has()
   */
  function listAcl(needle = '.', skipModCheck = false)
  {
    const reg = new RegExp(needle), result = []

    Object.keys(acl).forEach(prop => {
      if (reg.test(prop) && hasAcl(prop, 'and', skipModCheck)) {
        result.push(prop)
      }
    })

    return result;
  }

  /**
   * Discards the loaded ACL map so the next check loads the rights of the
   * current identity, and notifies consumers via aclRevision.
   *
   * @returns {void}
   */
  function resetACL ()
  {
    Object.keys(acl).forEach(key => {
      delete acl[key]
    })
    aclLoadPromise = null
    aclRevision.value++
  }

  /**
   * Loads the ACL map of the current identity once. Concurrent callers share one
   * request; a response that arrives after the identity changed is discarded.
   *
   * @returns {Promise<void>}
   */
  async function loadACL ()
  {
    if (Object.keys(acl).length !== 0) {
      return
    }

    if (!aclLoadPromise) {
      aclLoadPromise = fetchACL(aclRevision.value)
    }
    await aclLoadPromise
  }

  /**
   * Fetches the ACL map and stores it when the identity is still the one the
   * request was made for.
   *
   * @param {number} revision - aclRevision at request time.
   * @returns {Promise<void>}
   */
  async function fetchACL (revision)
  {
    try {
      const response = await api.get('/sessions/getAclProperties.json', {

      })
      if (revision !== aclRevision.value) {
        return
      }
      Object.keys(response.data).forEach(key => {
        acl[key] = response.data[key]
      })
      // eslint-disable-next-line no-unused-vars
    } catch (e) {
      if (revision === aclRevision.value) {
        // allow a retry on the next check
        aclLoadPromise = null
      }
      n.error(i18n.global.t('acl.load_error'))
    }
  }

  return {
    user,
    mandants,
    currentMandant,
    hasToken,
    login,
    logout,
    loginByTicket,
    changeUser,
    getAuthHeader,
    weakHasAcl,
    hasAcl,
    aclRevision,
    matchAcl,
    listAcl,
    saveCurrentModul,
    currentModul,
  }
})
